/**
 * External File Search Agent - Evaluation Section
 * Section 5: Final Reminders, Explanations, Evaluation Criteria
 *
 * Keywords AI Key: EXTERNAL_FILE_EVALUATION_CRITERIA
 */
import { PromptSectionBase } from '../../../base/systemPrompt/PromptSectionBase';
import { SECURITY_WARNING_SHORT } from '../../../base/systemPrompt/shared';
import { getSharedVariable } from '../../../../prompts';
import { EXTERNAL_FILE_SEARCH_PROMPT_KEYS } from './variables';
class ExternalFileSearchEvaluationSection extends PromptSectionBase {
    constructor() {
        super(...arguments);
        this.keywordsKey = EXTERNAL_FILE_SEARCH_PROMPT_KEYS.evaluation;
        this.fallbackContent = `## Example Workflow

1. **Search Call**:
\`\`\`json
{
  "searchType": "vector",
  "searchOperator": "or",
  "searchFiles": "all",
  "query": "revenue growth projections",
  "numResults": 5
}
\`\`\`

2. **Read Excel for Details** (if needed):
\`\`\`json
{
  "fileIndex": 0,
  "mode": "detailedValues",
  "range": "Sheet1!A1:D10"
}
\`\`\`

3. **Submit Results**:
\`\`\`json
{
  "results": [
    {
      "requirementId": "A",
      "relevantResults": {
        "searchResults": ["page_abc123", "excel_def45"]
      },
      "rationale": "Found revenue projections in Financial Model sheet (excel_def45) and Summary PDF page 5 (page_abc123). Both show 5-year growth forecast with CAGR 15-20%."
    }
  ]
}
\`\`\`

---

## Evaluation Criteria

Your performance is evaluated on:

### Search Effectiveness
- Did you choose the right search type for each query?
- Did you find the most relevant documents?
- Did you use readExcel appropriately for detailed data?

### Result Quality
- Are the document IDs correct and relevant?
- Do the rationales clearly explain what was found?
- Did you address ALL requirements?

### Completeness
- Did you call submitResults with proper structure?
- Are all relevant documents included?

---

## CRITICAL Rules

1. Search broadly first, then narrow down
2. Use appropriate search type for each requirement
3. Include ALL relevant document IDs in searchResults
4. Write clear rationale explaining what was found and where
5. Call submitResults ONCE after ALL requirements are fulfilled
6. For Excel files, use readExcel with fileIndex to get detailed data when search results indicate relevant content

${SECURITY_WARNING_SHORT}`;
    }
}
const evaluationSection = new ExternalFileSearchEvaluationSection();
/**
 * Get the evaluation section.
 * Uses Keywords AI if available, otherwise falls back to code.
 * Supports {{securityWarnings}} variable replacement.
 */
export function getEvaluationSection() {
    return evaluationSection.getContentWithVariables({
        securityWarnings: getSharedVariable('securityWarnings'),
    });
}
/**
 * Export constant for backwards compatibility
 */
export const EXTERNAL_FILE_EVALUATION = evaluationSection.getFallbackContent();
